import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';

/**
 * Outside-click dismissal for `isNonModal` popovers.
 *
 * Why this exists — the react-aria built-ins all fail inside our slideouts:
 *
 * 1. MODAL popovers (the react-aria default) dismiss via `useOverlay`'s
 *    interact-outside + a full-viewport underlay, and apply `inert` to
 *    everything outside the popover (`ariaHideOutside(…, {shouldUseInert})`).
 *    The underlay renders with `z-index: auto`, so inside any surface that
 *    sets an explicit stacking index (a slideout at z≈810, the popover itself
 *    at z≈900 via `useStackingZIndex`) it paints beneath the page.
 * 2. NON-MODAL popovers skip the underlay/inert, but `usePopover` passes
 *    `isDismissable: !isNonModal` to `useOverlay` — outside-POINTER dismissal
 *    is disabled entirely. Only `shouldCloseOnBlur` remains, and `useOverlay`
 *    deliberately ignores a blur whose `relatedTarget` is null (focus lost to
 *    `<body>`, react-spectrum #4130) — exactly what a click on empty,
 *    non-focusable page area produces.
 * 3. Decisively: react-aria's `useInteractOutside` ignores ANY event whose
 *    target sits inside a `[data-react-aria-top-layer]` subtree — and the
 *    non-modal slideout marks its whole panel with that attribute (its own
 *    escape hatch from a parent Modal's `ariaHideOutside`; see
 *    slideout-menu-nonmodal.tsx). So every click inside a slideout is
 *    invisible to every react-aria interact-outside handler, which is why
 *    popovers opened from the task slideout could never be dismissed at all.
 *
 * Hence this hook listens at the document itself (capture phase, mirroring
 * react-aria's pointerdown→click pairing so a drag that starts inside the
 * popover never dismisses it) and applies its own validity rules:
 *
 * - Interactions inside the popover element never dismiss.
 * - A press on the popover's OWN trigger (an element whose `aria-controls`
 *   targets a node inside this popover) dismisses AND swallows the press
 *   (preventDefault + stopPropagation at document capture — the same move
 *   react-aria's modal `onInteractOutsideStart` makes). Necessary because
 *   `useMenuTrigger`'s press handler only ever calls `state.open()` — react-
 *   aria relies on the modal underlay to close on trigger re-click, and
 *   non-modal has no underlay, so without the swallow the press would
 *   immediately reopen what we just closed.
 * - Exception: when the own-trigger element is already the focused, editable
 *   element (a ComboBox's input, where the trigger and the text field are the
 *   same node), a press on it is the user placing the caret or selecting text
 *   — not a re-open/toggle press — so it does NOT dismiss. Toggle-close-on-
 *   re-click only makes sense for button-style triggers (Select, Dropdown)
 *   that are a separate node from their popover.
 * - Any other outside interaction dismisses WITHOUT being swallowed, so the
 *   click still lands (click-through): pressing the Status trigger closes an
 *   open Due-date calendar and opens the Status menu in one click.
 * - Unlike react-aria, top-layer content does NOT get a free pass: clicking a
 *   toast / hover-popover / floating toolbar while a picker is open dismisses
 *   the picker, which is the standard lightweight-picker behavior.
 */
export function useDismissOnInteractOutside(
  ref: RefObject<Element | null>,
  isOpen: boolean,
  onDismiss: () => void
): void {
  // Latest-callback ref so the document listeners never hold a stale closure
  // and the effect doesn't need onDismiss in its dep array (re-subscribing on
  // every render would drop the pointerdown/click pairing state mid-gesture).
  const onDismissRef = useRef(onDismiss);
  useEffect(() => {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    let pointerDownWasOutside = false;

    // Does this element's `aria-controls` point at a node inside the popover?
    // That identifies the popover's OWN trigger (react-aria stamps
    // aria-controls with the id of the controlled menu/dialog while open).
    const controlsThisPopover = (el: Element, popover: Element): boolean => {
      const controls = el.getAttribute('aria-controls');
      if (!controls) {
        return false;
      }
      return controls.split(/\s+/).some(id => {
        const controlled = document.getElementById(id);
        return (
          controlled != null &&
          (controlled === popover || popover.contains(controlled))
        );
      });
    };

    const isOutside = (e: Event): boolean => {
      const el = ref.current;
      if (!el) {
        return false;
      }
      return !e.composedPath().includes(el);
    };

    const onPointerDown = (e: PointerEvent) => {
      pointerDownWasOutside = false;
      if (e.button > 0) {
        return;
      }
      const el = ref.current;
      if (!el || !isOutside(e)) {
        return;
      }
      const ownTrigger = e
        .composedPath()
        .find(
          node => node instanceof Element && controlsThisPopover(node, el)
        ) as Element | undefined;
      if (ownTrigger) {
        // A press on an already-focused, editable own-trigger (a ComboBox's
        // input) is text-selection/caret-placement, not a toggle press —
        // let it proceed without dismissing (see the doc-comment exception).
        const isEditingOwnTrigger =
          ownTrigger === document.activeElement &&
          (ownTrigger instanceof HTMLInputElement ||
            ownTrigger instanceof HTMLTextAreaElement);
        if (isEditingOwnTrigger) {
          return;
        }
        // Close now and swallow the press so the trigger's open-on-press
        // handler never runs (see the own-trigger note in the doc comment).
        e.preventDefault();
        e.stopPropagation();
        onDismissRef.current();
        return;
      }
      pointerDownWasOutside = true;
    };
    const onClick = (e: MouseEvent) => {
      if (pointerDownWasOutside && isOutside(e)) {
        onDismissRef.current();
      }
      pointerDownWasOutside = false;
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('click', onClick, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('click', onClick, true);
    };
  }, [isOpen, ref]);
}
